import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/hooks/useAuth';
import { StripeConnectStatus } from '@/types/billing';

/**
 * Hook to check the Stripe Connect status of the current organization
 * This determines whether auto-billing features should be available
 */
export const useStripeConnectStatus = () => {
  const { user } = useAuth();
  const organizationId = user?.user_metadata?.organization_id;

  return useQuery<StripeConnectStatus>({
    queryKey: ['stripe-connect-status', organizationId],
    queryFn: async (): Promise<StripeConnectStatus> => {
      console.log('=== useStripeConnectStatus queryFn RUNNING with organizationId:', organizationId);
      
      if (!organizationId) {
        return {
          isConnected: false,
          isActive: false,
          hasError: false,
          accountId: null,
          status: 'not_connected'
        };
      }

      try {
        // Get organization billing settings
        const { data: orgData, error: orgError } = await supabase
          .from('organizations')
          .select('billing_settings')
          .eq('id', organizationId)
          .single();

        if (orgError) {
          console.error('Error fetching organization billing settings:', orgError);
          return {
            isConnected: false,
            isActive: false,
            hasError: true,
            accountId: null,
            status: 'error',
            errorMessage: 'Failed to fetch organization settings'
          };
        }

        const billingSettings = orgData?.billing_settings || {};
        const stripeAccountId = billingSettings.stripe_account_id;

        if (!stripeAccountId) {
          return {
            isConnected: false,
            isActive: false,
            hasError: false,
            accountId: null,
            status: 'not_connected'
          };
        }

        // Check if the Stripe Connect account is active by trying to get balance
        // This will validate that the account is properly connected and functional
        try {
          console.log('=== About to call get-stripe-balance with organizationId:', organizationId);
          
          // Use direct fetch with proper auth to bypass Supabase client caching
          const { data: { session } } = await supabase.auth.getSession();
          const supabaseUrl = import.meta.env.VITE_SUPABASE_URL || 'https://muoihzfomfercsujyyta.supabase.co';
          const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY || 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6Im11b2loemZvbWZlcmNzdWp5eXRhIiwicm9sZSI6ImFub24iLCJpYXQiOjE3MzQ2MDQ4MzIsImV4cCI6MjA1MDE4MDgzMn0.H8jP9lY1M2xF5_O1QrqzJhQq0RgJKc1ydRJx7YXFTdU';
          
          const response = await fetch(`${supabaseUrl}/functions/v1/get-stripe-balance`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${session?.access_token}`,
              'apikey': supabaseAnonKey,
              'Cache-Control': 'no-cache'
            },
            body: JSON.stringify({ organizationId })
          });
          
          const balanceData = await response.json();
          const balanceError = response.ok ? null : balanceData;
          
          console.log('=== get-stripe-balance result:', { balanceData, balanceError });

          if (balanceError) {
            return {
              isConnected: true,
              isActive: false,
              hasError: true,
              accountId: stripeAccountId,
              status: 'error',
              errorMessage: 'Stripe Connect account has issues'
            };
          }

          const isActive = balanceData?.isActive === true;
          
          console.log('=== Final status result:', {
            isConnected: true,
            isActive,
            hasError: !isActive,
            accountId: stripeAccountId,
            status: isActive ? 'connected' : 'pending',
            balanceDataIsActive: balanceData?.isActive
          });
          
          return {
            isConnected: true,
            isActive,
            hasError: !isActive,
            accountId: stripeAccountId,
            status: isActive ? 'connected' : 'pending',
            requiresAction: !isActive,
            requiresOnboarding: balanceData?.requiresOnboarding,
            errorMessage: balanceData?.error
          };

        } catch (balanceCheckError) {
          // If balance check fails, assume the account exists but may need action
          return {
            isConnected: true,
            isActive: false,
            hasError: true,
            accountId: stripeAccountId,
            status: 'pending',
            requiresAction: true,
            errorMessage: 'Unable to verify Stripe Connect status'
          };
        }

      } catch (error) {
        console.error('Error checking Stripe Connect status:', error);
        return {
          isConnected: false,
          isActive: false,
          hasError: true,
          accountId: null,
          status: 'error',
          errorMessage: 'Failed to check Stripe Connect status'
        };
      }
    },
    enabled: !!organizationId,
    // Temporarily reduce cache time for debugging
    staleTime: 0, // Always consider stale
    cacheTime: 0, // Don't cache
    // But allow background refetch to keep status current
    refetchInterval: false // Disable auto refetch for now
  });
};

/**
 * Helper function to determine if auto-billing features should be enabled
 */
export const useCanUseAutoBilling = () => {
  const { data: stripeStatus, isLoading } = useStripeConnectStatus();
  
  return {
    canUseAutoBilling: stripeStatus?.isActive === true,
    isLoading,
    stripeStatus
  };
};